import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { DEFAULT_DAYS_OF_WITNESSES_DATA } from ".";
import InfoIcon from "../../../../components/icons/InfoIcon";
import { useHotspots } from "../../../../context/hotspots";
import { Entity } from "../../../../utils/constants";
import {
  WitnessesVisualizationKey,
  WitnessesVisualizationVariant
} from "../../../../utils/useMapConfig";
import StatisticsCardWithRelativePerformance from "../../common/StatisticsCardWithRelativePerformance";
import VisualizationToggle from "../../common/VisualizationToggle";

const BidirectionalWitnessesTab: React.FC<
  RouteComponentProps<{ id: string }>
> = () => {
  const {
    currentHotspot,
    hotspotWitnessesCountsMap,
    getDataPeriodDescription
  } = useHotspots();

  const dataPeriodDescription = useMemo(
    () =>
      currentHotspot?.id &&
      getDataPeriodDescription(
        currentHotspot.id,
        DEFAULT_DAYS_OF_WITNESSES_DATA
      ),
    [currentHotspot?.id, getDataPeriodDescription]
  );

  const hotspotWitnessesCounts = useMemo(
    () => currentHotspot && hotspotWitnessesCountsMap[currentHotspot?.id],
    [currentHotspot, hotspotWitnessesCountsMap]
  );

  if (!currentHotspot || !hotspotWitnessesCounts) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-wrap mb-2 w-full">
          <div className="flex w-full sm:w-28 h-9 mb-4 animate-pulse bg-gray-400 rounded-lg" />
          <div className="hidden sm:flex w-28 h-9 mx-4 mb-4 animate-pulse bg-gray-400 rounded-lg" />
          <div className="hidden sm:flex w-28 h-9 mb-4 animate-pulse bg-gray-400 rounded-lg" />
        </div>
        <div className="flex flex-col sm:flex-row w-full items-start">
          <div className="flex w-full sm:w-4/12 h-28 animate-pulse bg-gray-400 rounded-lg" />
          <div className="flex w-full sm:w-4/12 my-5 sm:my-0 sm:ml-5 h-36 animate-pulse bg-gray-400 rounded-lg" />
          <div className="flex w-full sm:w-4/12 sm:ml-5 h-28 animate-pulse bg-gray-400 rounded-lg" />
        </div>
      </div>
    );
  }

  // TODO: fetch missing hotspots

  return (
    <>
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div>
          <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center justify-between">
                <div className="flex items-center">
                  <span>All witnesses</span>
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        <div>{`This is the sum of all the hotspots that were able to witness each others beacons ${dataPeriodDescription}.`}</div>
                        <div>{`Red dots represent hotspots with challenges that had at least one invalid witness ${dataPeriodDescription}.`}</div>
                        <div>
                          The green dots represent hotspots for which all
                          witnessing activity was valid.
                        </div>
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </div>

                <VisualizationToggle
                  witnessesVisualizationKey={
                    WitnessesVisualizationKey.bidirectional
                  }
                  visualisationVariant={WitnessesVisualizationVariant.all}
                />
              </dt>

              <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {hotspotWitnessesCounts.bidirectional.all}
              </dd>
            </div>
          </div>
        </div>

        <div>
          <StatisticsCardWithRelativePerformance
            id={currentHotspot.id}
            entity={Entity.hotspot}
            title={
              <>
                <div className="flex items-center">
                  <span>Valid witnesses</span>
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        <div>{`This is the sum of all the hotspots that were able to witness each others beacons in a valid way at least one time ${dataPeriodDescription}.`}</div>
                        <div>
                          Rewards for transmitted beacons are scaled down by the
                          transmit scale. The hotspots are colored by the lowest
                          transmit scale of the connecting hotspots.
                        </div>
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </div>

                <VisualizationToggle
                  witnessesVisualizationKey={
                    WitnessesVisualizationKey.bidirectional
                  }
                  visualisationVariant={WitnessesVisualizationVariant.valid}
                />
              </>
            }
            metricIteratee="witnessesSummary.bidirectional.valid"
          >
            {hotspotWitnessesCounts.bidirectional.valid}
          </StatisticsCardWithRelativePerformance>
        </div>

        <div>
          <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center justify-between">
                <div className="flex items-center">
                  <span>Invalid witnesses</span>
                  <Tooltip
                    placement="top"
                    overlay={`This is the sum of all the hotspots that were able to witness each others beacons but were at least once invalid ${dataPeriodDescription}.`}
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </div>

                <VisualizationToggle
                  witnessesVisualizationKey={
                    WitnessesVisualizationKey.bidirectional
                  }
                  visualisationVariant={WitnessesVisualizationVariant.invalid}
                />
              </dt>

              <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {hotspotWitnessesCounts.bidirectional.invalid}
              </dd>
            </div>
          </div>
        </div>
      </dl>
    </>
  );
};

export default BidirectionalWitnessesTab;
